// crewbit-admin/src/components/money/AutoSaveIndicator.tsx
//
// Spec: §InvoiceAuthoring.7.4. Top-right pill — saving / saved / failed.
// Reads {isAutoSaving, lastSavedAt, error} from useInvoiceDraft.
// Mirrors crewbit-mobile/components/money/authoring/AutoSaveIndicator.tsx.

import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../styles/tokens';

interface Props {
  isAutoSaving: boolean;
  lastSavedAt: string | null;
  error: Error | null;
  onRetry?: () => void;
}

function formatRelative(iso: string, now: number): string {
  const ms = now - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

const PILL_BASE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: `4px ${spacing.sm}px`,
  borderRadius: radii.full,
  fontSize: typography.size.caption,
  fontFamily: typography.fontFamily,
  fontVariantNumeric: 'tabular-nums',
  border: 'none',
  cursor: 'default',
};

export function AutoSaveIndicator({ isAutoSaving, lastSavedAt, error, onRetry }: Props) {
  // Lazy-init Date.now() inside useState initializer (purity-safe per
  // react-hooks rules); refresh every 15s so relative times stay current.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  if (error) {
    return (
      <button
        type="button"
        onClick={onRetry}
        style={{
          ...PILL_BASE,
          background: palette.redWash,
          color: palette.red,
          cursor: onRetry ? 'pointer' : 'default',
          fontWeight: typography.weight.semibold,
        }}
      >
        <AlertTriangle size={12} color={palette.red} strokeWidth={2} />
        Save failed — retry
      </button>
    );
  }

  if (isAutoSaving) {
    return (
      <span
        style={{
          ...PILL_BASE,
          background: palette.g100,
          color: palette.g600,
        }}
      >
        <Loader2 size={12} color={palette.g500} strokeWidth={2} className="autosave-spin" />
        <style>{`@keyframes autosave-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .autosave-spin { animation: autosave-spin 1s linear infinite; }`}</style>
        Saving…
      </span>
    );
  }

  if (lastSavedAt) {
    const rel = formatRelative(lastSavedAt, nowMs);
    return (
      <span
        style={{
          ...PILL_BASE,
          background: palette.greenWash,
          color: palette.green,
        }}
      >
        <CheckCircle size={12} color={palette.green} strokeWidth={2} />
        Saved {rel}
      </span>
    );
  }

  return null;
}
